// Imports
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { RestStop } from '../../models/rest-stop';
import { MatIconModule } from '@angular/material/icon';
import { AsyncPipe, DecimalPipe, DOCUMENT, NgForOf } from '@angular/common';
import { MatDivider } from '@angular/material/divider';
import { MatIconButton } from '@angular/material/button';
import { ReviewService } from '../../services/review.service';
import { map, Observable, shareReplay, take, tap } from 'rxjs';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ReviewSectionComponent } from '../review-section/review-section.component';
import { Review } from '../../models/Review';
import { FavoritesService } from '../../services/favorites.service';
import { StateSnackBarService } from '../../services/state-snack-bar.service';
import { AuthService } from '../../services/auth.service';
import { BottomSheetService } from '../../services/bottom-sheet.service';
import { RestStopPhotosComponent } from '../reststops/rest-stop-photos/rest-stop-photos.component';
import { MatTabsModule } from '@angular/material/tabs';

@Component({
  selector: 'app-peek-bottom-sheet',
  standalone: true,
  imports: [
    MatIconModule,
    DecimalPipe,
    NgForOf,
    MatDivider,
    MatIconButton,
    MatTabsModule,
    ReactiveFormsModule,
    ReviewSectionComponent,
    AsyncPipe,
    RestStopPhotosComponent,
  ],
  templateUrl: './peek-bottom-sheet.component.html',
  styleUrls: ['./peek-bottom-sheet.component.scss'],
})
export class PeekBottomSheetComponent implements AfterViewInit, OnDestroy, OnChanges {
  @Input() restStop!: RestStop;
  @Output() closed = new EventEmitter<void>();

  @ViewChild('expandedContent') expandedContentRef!: ElementRef<HTMLElement>;

  reviewService = inject(ReviewService);
  private fb = inject(FormBuilder);
  private snackBarService = inject(StateSnackBarService);
  public authService = inject(AuthService);
  private bottomSheetService = inject(BottomSheetService);
  public activeTabIndex = 0;
  reviews$!: Observable<Review[]>;
  otherReviews$!: Observable<Review[]>;

  reviewForm: FormGroup;
  hoverRating = 0;

  constructor(@Inject(DOCUMENT) private document: Document) {
    this.reviewForm = this.fb.group({
      rating: [0],
      comment: [''],
    });
  }

  public favoritesService = inject(FavoritesService);
  isFavorite$!: Observable<boolean>;
  private ownReviewId: number | null = null;
  public ownReviewStatus: 'approved' | 'pending' | 'none' = 'none';
  currentState: 'peek' | 'expanded' = 'peek';

  isDragging = false;
  private dragStartY = 0;
  private dragStartTranslateY = 0;
  private dragCurrentTranslateY = 0;

  get peekTranslateY(): number {
    return window.innerHeight - 120;
  }
  get expandedTranslateY(): number {
    return 80;
  }

  get dragTransform(): string | null {
    return this.isDragging ? `translateY(${this.dragCurrentTranslateY}px)` : null;
  }

  get staticTransform(): string {
    const y = this.currentState === 'peek' ? this.peekTranslateY : this.expandedTranslateY;
    return `translateY(${y}px)`;
  }

  get transitionStyle(): string {
    return `transform ${this.transitionMs}ms ease-in-out`;
  }

  get dragProgress(): number {
    if (this.isClosing) {
      return 0;
    }
    const min = this.expandedTranslateY;
    const max = this.peekTranslateY;
    let y: number;
    if (this.isDragging) {
      y = this.dragCurrentTranslateY;
    } else {
      y = this.currentState === 'peek' ? max : min;
    }
    const clamped = Math.max(min, Math.min(max, y));
    const denom = max - min || 1;
    return (max - clamped) / denom;
  }

  get miniOpacity(): number {
    return 1 - this.easeOutCubic(this.dragProgress);
  }

  get expandedOpacity(): number {
    return this.easeOutCubic(this.dragProgress);
  }

  get backdropOpacity(): number {
    return this.expandedOpacity;
  }

  get backdropPointerEvents(): 'auto' | 'none' {
    return this.dragProgress > 0.5 ? 'auto' : 'none';
  }

  private easeOutCubic(t: number): number {
    if (t <= 0) return 0;
    if (t >= 1) return 1;
    const inv = 1 - t;
    return 1 - inv * inv * inv;
  }

  sheetTransform: string = `translateY(${typeof window !== 'undefined' ? window.innerHeight + 200 : 1000}px)`;
  sheetTransition: string = 'none';

  toggleState(): void {
    this.currentState === 'peek' ? this.expand() : this.collapse();
  }

  expand(): void {
    this.currentState = 'expanded';
    this.sheetTransform = this.staticTransform;
    this.sheetTransition = this.transitionStyle;
  }

  collapse(): void {
    this.currentState = 'peek';
    this.sheetTransform = this.staticTransform;
    this.sheetTransition = this.transitionStyle;
  }

  closeOnBackdropClick(): void {
    if (this.currentState === 'expanded') {
      this.requestClose();
    }
  }

  private transitionMs = 300;
  isClosing = false;
  private mediaQueryList?: MediaQueryList;
  private openTimeoutId: any = null;
  private closeTimeoutId: any = null;
  private openingTimeoutId: any = null;
  private resizeHandler = () => this.onResize();

  requestClose(): void {
    if (this.isClosing) return;
    this.isClosing = true;
    this.bottomSheetService.startClosing();
    this.isDragging = false;
    this.sheetTransition = this.transitionStyle;
    this.sheetTransform = this.offscreenTranslate;
    this.closeTimeoutId = setTimeout(() => {
      this.closed.emit();
    }, this.transitionMs);
  }

  ngAfterViewInit(): void {
    if (typeof window !== 'undefined' && 'matchMedia' in window) {
      this.mediaQueryList = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.applyReducedMotion(this.mediaQueryList?.matches ?? false);
      const handler = (e: MediaQueryListEvent) => this.applyReducedMotion(e.matches);
      (this.mediaQueryList as any)._handler = handler;
      this.mediaQueryList.addEventListener('change', handler);
    }
    if (this.transitionMs <= 1) {
      this.sheetTransform = this.staticTransform;
      this.sheetTransition = 'none';
      this.openingTimeoutId = setTimeout(() => this.bottomSheetService.endOpening(), 0);
    } else {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.sheetTransform = this.staticTransform;
          this.sheetTransition = this.transitionStyle;
          this.openingTimeoutId = setTimeout(() => this.bottomSheetService.endOpening(), this.transitionMs);
        });
      });
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.resizeHandler);
    }
  }

  onDragStart(event: PointerEvent | TouchEvent): void {
    if (this.isDragging) return;
    let startY: number;
    if (event instanceof TouchEvent) {
      startY = event.touches[0].clientY;
    } else {
      startY = event.clientY;
    }
    this.isDragging = true;
    this.dragStartY = startY;
    this.dragStartTranslateY = this.currentState === 'peek' ? this.peekTranslateY : this.expandedTranslateY;
    this.dragCurrentTranslateY = this.dragStartTranslateY;
    if (event instanceof PointerEvent) {
      window.addEventListener('pointermove', this.onDragMove as any, { passive: false });
      window.addEventListener('pointerup', this.onDragEnd as any);
    } else {
      window.addEventListener('touchmove', this.onDragMove as any, { passive: false });
      window.addEventListener('touchend', this.onDragEnd as any);
    }
  }

  onDragMove = (event: any): void => {
    if (!this.isDragging) return;
    let clientY: number;
    if (event.touches && event.touches[0]) {
      clientY = event.touches[0].clientY;
    } else {
      clientY = event.clientY;
    }
    const dy = clientY - this.dragStartY;
    const target = this.dragStartTranslateY + dy;
    const min = this.expandedTranslateY;
    const max = this.peekTranslateY + 60;
    this.dragCurrentTranslateY = Math.max(min, Math.min(max, target));
    if (event.cancelable) event.preventDefault();
  };

  onDragEnd = (_event: any): void => {
    if (!this.isDragging) return;
    this.isDragging = false;
    const dismissThreshold = this.peekTranslateY + 40;
    const middle = (this.peekTranslateY + this.expandedTranslateY) / 2;
    const finalY = this.dragCurrentTranslateY;
    if (finalY >= dismissThreshold) {
      this.requestClose();
    } else if (finalY <= middle) {
      this.expand();
    } else {
      this.collapse();
    }
    if (!this.isClosing) {
      this.sheetTransform = this.staticTransform;
      this.sheetTransition = this.transitionStyle;
    }
    window.removeEventListener('pointermove', this.onDragMove as any);
    window.removeEventListener('pointerup', this.onDragEnd as any);
    window.removeEventListener('touchmove', this.onDragMove as any);
    window.removeEventListener('touchend', this.onDragEnd as any);
  };

  private get offscreenTranslate(): string {
    if (typeof window === 'undefined') return 'translateY(1000px)';
    return `translateY(${window.innerHeight * 2 + 400}px)`;
  }

  private applyReducedMotion(reduce: boolean) {
    this.transitionMs = reduce ? 1 : 300;
  }

  private onResize(): void {
    if (this.isClosing) return;
    if (this.isDragging) {
      const min = this.expandedTranslateY;
      const max = this.peekTranslateY + 60;
      this.dragCurrentTranslateY = Math.max(min, Math.min(max, this.dragCurrentTranslateY));
      return;
    }
    this.sheetTransition = 'none';
    this.sheetTransform = this.staticTransform;
    requestAnimationFrame(() => {
      this.sheetTransition = this.transitionStyle;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['restStop'] && this.restStop) {
      this.isFavorite$ = this.favoritesService.favorites$.pipe(
        map((favorites) => favorites.includes(this.restStop.id)),
      );
    }
  }

  ngOnDestroy(): void {
    try {
      window.removeEventListener('pointermove', this.onDragMove as any);
      window.removeEventListener('pointerup', this.onDragEnd as any);
      window.removeEventListener('touchmove', this.onDragMove as any);
      window.removeEventListener('touchend', this.onDragEnd as any);
    } catch {}
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.resizeHandler);
    }
    if (this.mediaQueryList) {
      const handler = (this.mediaQueryList as any)._handler;
      if (handler) this.mediaQueryList.removeEventListener('change', handler);
    }
    if (this.openTimeoutId) clearTimeout(this.openTimeoutId);
    if (this.closeTimeoutId) clearTimeout(this.closeTimeoutId);
    if (this.openingTimeoutId) clearTimeout(this.openingTimeoutId);
    this.bottomSheetService.endOpening();
  }

  get allAvailableFeatures() {
    const features = [];
    if (!this.restStop || !this.restStop.details) return [];
    if (this.restStop.details.restaurant) features.push({ icon: 'restaurant', label: 'Restaurant' });
    if (this.restStop.details.gasStation) features.push({ icon: 'local_gas_station', label: 'Tankstelle' });
    if (this.restStop.details.playground) features.push({ icon: 'child_care', label: 'Spielplatz' });
    if (this.restStop.details.shower) features.push({ icon: 'shower', label: 'Dusche' });
    if (this.restStop.details.wifi) features.push({ icon: 'wifi', label: 'WLAN' });
    if (this.restStop.details.kiosk) features.push({ icon: 'storefront', label: 'Kiosk' });
    if (this.restStop.details.picnicArea) features.push({ icon: 'deck', label: 'Picknick-Bereich' });
    if (this.restStop.details.chargingStation) features.push({ icon: 'ev_station', label: 'E-Ladestation' });
    if (this.restStop.details.atm) features.push({ icon: 'credit_card', label: 'Geldautomat' });
    return features;
  }

  getStarRatingArray(rating: number): string[] {
    const stars = [];
    const roundedRating = Math.round(rating * 2) / 2;
    for (let i = 1; i <= 5; i++) {
      if (roundedRating >= i) {
        stars.push('star');
      } else if (roundedRating >= i - 0.5) {
        stars.push('star_half');
      } else {
        stars.push('star_border');
      }
    }
    return stars;
  }

  loadReviews(): void {
    const currentUserId = this.authService.currentUserValue?.username;
    this.reviews$ = this.reviewService.getReviews(this.restStop.id).pipe(
      take(1),
      tap((reviews) => {
        const ownReview = reviews.find((r) => r.username === currentUserId);
        if (ownReview) {
          this.ownReviewId = ownReview.id;
          this.reviewForm.patchValue({
            rating: ownReview.rating,
            comment: ownReview.comment,
          });
          if (ownReview.approved) {
            this.ownReviewStatus = 'approved';
            this.reviewForm.enable();
          } else {
            this.ownReviewStatus = 'pending';
            this.reviewForm.disable();
          }
        } else {
          this.ownReviewStatus = 'none';
          this.reviewForm.enable();
        }
      }),
      shareReplay(1),
    );
    this.otherReviews$ = this.reviews$.pipe(map((reviews) => reviews.filter((r) => r.username !== currentUserId)));
  }

  setRating(rating: number): void {
    if (this.ownReviewStatus !== 'pending') {
      this.reviewForm.get('rating')?.setValue(rating);
    }
  }

  submitReview(): void {
    if (this.reviewForm.get('rating')?.value < 1) {
      this.snackBarService.error({ message: 'Bitte gib eine Bewertung von 1-5 Sternen an.' });
      return;
    }
    if (this.reviewForm.invalid) {
      this.snackBarService.error({ message: 'Bitte gib eine Bewertung von 1-5 Sternen an.' });
      return;
    }
    const { rating, comment } = this.reviewForm.value;
    const request$ = this.ownReviewId
      ? this.reviewService.updateReview(this.restStop.id, this.ownReviewId, rating, comment)
      : this.reviewService.postReview(this.restStop.id, rating, comment);
    request$.subscribe({
      next: () => {
        const message = this.ownReviewId
          ? 'Deine geänderte Bewertung wird nach Prüfung freigeschaltet.'
          : 'Deine Bewertung wird nach Prüfung freigeschaltet.';
        this.snackBarService.success({ message: `Danke! ${message}` });
      },
      error: () => {
        this.snackBarService.error({ message: 'Fehler beim Senden der Bewertung.' });
      },
    });
  }

  get isMobile(): boolean {
    if (typeof window === 'undefined') return false;
    const ua = navigator.userAgent || '';
    const mobileRegex = /(Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini)/i;
    const hasTouch = (navigator as any).maxTouchPoints && (navigator as any).maxTouchPoints > 1;
    return mobileRegex.test(ua) || !!hasTouch;
  }

  get mapsHref(): string {
    if (!this.restStop) return '#';
    const lat = this.restStop.lat;
    const lng = this.restStop.lng;
    const name = encodeURIComponent(this.restStop.name ?? '');
    if (this.isMobile) {
      return `geo:${lat},${lng}?q=${name}`;
    }
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  }
}
